/**
 * @fileoverview Publish-Subscribe Event System
 * @description Implements a simple publish-subscribe pattern for event handling
 *
 * Features:
 * - Event subscription management
 * - Event publishing
 * - Automatic subscriber cleanup
 * - Multiple subscribers per event
 *
 * Usage:
 * ```javascript
 * // Subscribe to an event
 * const unsubscribe = subscribe('eventName', (data) => {
 *   console.log(data);
 * });
 *
 * // Publish an event
 * publish('eventName', { data: 'value' });
 *
 * // Unsubscribe
 * unsubscribe();
 * ```
 */

/**
 * @type {Object.<string, Function[]>}
 * @private
 */
let subscribers = {};

/**
 * @function subscribe
 * @description Subscribes a callback function to an event
 * @param {string} eventName - The name of the event to subscribe to
 * @param {Function} callback - The function to call when the event is published
 * @returns {Function} A function to unsubscribe the callback
 */
function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

/**
 * @function publish
 * @description Publishes an event to all subscribers
 * @param {string} eventName - The name of the event to publish
 * @param {*} data - The data to pass to the subscribers
 */
function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}
